/**
 * NeoCoreCatalogoContablePage — catálogo contable INGECOP para que el contador
 * (Carlos) lo revise y valide ("puntee") cuenta por cuenta.
 *
 * - Resumen arriba (total, oficiales, validadas, con movimientos, por clase).
 * - Buscador + filtros (clase, solo con movimientos, solo sin validar).
 * - Checkbox "validada" por cuenta → RPC validar_cuenta_contable_admin.
 *
 * Fuente: tabla cuenta_contable (catálogo oficial cargado, migración 0258) +
 * RPC reporte_catalogo_contable_admin. Solo admin.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, MagnifyingGlass, CheckCircle, Circle, PencilSimple, Plus, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Cuenta = {
  codigo: string
  nombre: string
  clase: string
  nivel: number
  naturaleza: string
  acepta_partidas: boolean
  es_ingecop_oficial: boolean
  validado_ingecop_at: string | null
  n_partidas: number
}
type Resumen = {
  total: number
  oficiales: number
  validadas: number
  con_movimientos: number
  por_clase: { clase: string; n: number; validadas: number }[]
}

const CLASES: Record<string, string> = {
  '1': 'Activo', '2': 'Regularizadoras', '3': 'Pasivo', '4': 'Otras acreedoras',
  '5': 'Capital', '6': 'Productos', '7': 'Gastos', '8': 'Contingencias', '9': 'Cuentas de orden',
}

export function NeoCoreCatalogoContablePage() {
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [rows, setRows] = useState<Cuenta[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [clase, setClase] = useState('')
  const [soloMov, setSoloMov] = useState(false)
  const [soloSinValidar, setSoloSinValidar] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editando, setEditando] = useState<Cuenta | null>(null)
  const [creando, setCreando] = useState(false)

  const loadResumen = useCallback(() => {
    supabase.rpc('reporte_catalogo_contable_admin').then(({ data, error }) => {
      if (!error) setResumen(data as Resumen)
    })
  }, [])

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Códigos con movimientos (para marcar / filtrar)
    const { data: mov } = await supabase.from('partida_asiento').select('cuenta_codigo')
    const conMov = new Map<string, number>()
    ;(mov ?? []).forEach((m: { cuenta_codigo: string }) => {
      conMov.set(m.cuenta_codigo, (conMov.get(m.cuenta_codigo) ?? 0) + 1)
    })

    let query = supabase
      .from('cuenta_contable')
      .select('codigo,nombre,clase,nivel,naturaleza,acepta_partidas,es_ingecop_oficial,validado_ingecop_at')
      .order('codigo', { ascending: true })
      .limit(1000)
    if (clase) query = query.eq('clase', clase)
    if (q.trim()) query = query.or(`codigo.ilike.%${q.trim()}%,nombre.ilike.%${q.trim()}%`)
    if (soloSinValidar) query = query.is('validado_ingecop_at', null)

    const { data, error: err } = await query
    if (err) { setError(err.message); setLoading(false); return }
    let list = (data ?? []).map((r: any) => ({ ...r, n_partidas: conMov.get(r.codigo) ?? 0 })) as Cuenta[]
    if (soloMov) list = list.filter((r) => r.n_partidas > 0)
    setRows(list)
    setLoading(false)
  }, [q, clase, soloMov, soloSinValidar])

  useEffect(() => { loadResumen() }, [loadResumen])
  useEffect(() => {
    const t = setTimeout(() => { loadRows() }, 250)
    return () => clearTimeout(t)
  }, [loadRows])

  const toggleValidar = async (c: Cuenta) => {
    const nuevo = !c.validado_ingecop_at
    setRows((prev) => prev.map((r) => r.codigo === c.codigo
      ? { ...r, validado_ingecop_at: nuevo ? new Date().toISOString() : null } : r))
    const { error } = await supabase.rpc('validar_cuenta_contable_admin', { p_codigo: c.codigo, p_validado: nuevo })
    if (error) {
      setRows((prev) => prev.map((r) => r.codigo === c.codigo
        ? { ...r, validado_ingecop_at: c.validado_ingecop_at } : r))
      alert('No se pudo validar: ' + error.message)
    } else {
      loadResumen()
    }
  }

  const pct = useMemo(() => {
    if (!resumen || resumen.total === 0) return 0
    return Math.round((resumen.validadas / resumen.total) * 100)
  }, [resumen])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ margin: 0, color: 'var(--n-500)', fontSize: 13 }}>
        Catálogo oficial INGECOP. Revisá cada cuenta y marcala como validada. Empezá por las que tienen movimientos.
      </p>

      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
          <Kpi label="Cuentas totales" value={resumen.total} />
          <Kpi label="Oficiales cargadas" value={resumen.oficiales} />
          <Kpi label="Validadas" value={resumen.validadas} sub={`${pct}% del catálogo`} />
          <Kpi label="Con movimientos" value={resumen.con_movimientos} sub="prioridad de revisión" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--n-400)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar código o nombre…"
            style={{ width: '100%', paddingLeft: 32, height: 36 }} />
        </div>
        <select value={clase} onChange={(e) => setClase(e.target.value)} style={{ height: 36 }}>
          <option value="">Todas las clases</option>
          {Object.entries(CLASES).map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={soloMov} onChange={(e) => setSoloMov(e.target.checked)} /> Con movimientos
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={soloSinValidar} onChange={(e) => setSoloSinValidar(e.target.checked)} /> Sin validar
        </label>
        <button className="btn btn-ghost" onClick={() => { loadRows(); loadResumen() }}><ArrowsClockwise size={16} /></button>
        <button className="btn" onClick={() => setCreando(true)}><Plus size={16} /> Nueva cuenta</button>
      </div>

      {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Código', 'Nombre', 'Clase', 'Mov.', 'Validada', 'Editar'].map((h, i) => (
                  <th key={h} style={{ textAlign: i > 2 ? 'center' : 'left', padding: '10px 14px', fontSize: 11, color: 'var(--n-500)', borderBottom: '1px solid var(--n-100)', position: 'sticky', top: 0, background: 'var(--surface-2, #fff)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--n-400)' }}>Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--n-400)' }}>Sin resultados.</td></tr>
              ) : rows.map((c) => {
                const val = !!c.validado_ingecop_at
                return (
                  <tr key={c.codigo} style={{ background: c.n_partidas > 0 ? 'var(--brand-50, #FFF7F2)' : undefined }}>
                    <td style={{ padding: '8px 14px', fontFamily: 'monospace', borderBottom: '1px solid var(--n-50)' }}>{c.codigo}</td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--n-50)', paddingLeft: 14 + (c.nivel - 1) * 6 }}>
                      {c.acepta_partidas ? c.nombre : <strong>{c.nombre}</strong>}
                    </td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--n-50)', color: 'var(--n-500)', fontSize: 12 }}>{c.clase} · {CLASES[c.clase] ?? ''}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'center', borderBottom: '1px solid var(--n-50)' }}>{c.n_partidas > 0 ? c.n_partidas : '—'}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'center', borderBottom: '1px solid var(--n-50)' }}>
                      <button onClick={() => toggleValidar(c)} title={val ? 'Validada' : 'Marcar validada'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: val ? 'var(--ok, #1E9E63)' : 'var(--n-300)' }}>
                        {val ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} />}
                      </button>
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'center', borderBottom: '1px solid var(--n-50)' }}>
                      <button onClick={() => setEditando(c)} title="Corregir cuenta"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--n-400)' }}>
                        <PencilSimple size={18} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Filas resaltadas = cuentas con movimientos (revisar primero). Máx. 1000 filas por búsqueda; usá los filtros para acotar.
      </p>

      {editando && (
        <EditarCuentaModal
          cuenta={editando}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); loadRows(); loadResumen() }}
        />
      )}
      {creando && (
        <CrearCuentaModal
          onClose={() => setCreando(false)}
          onSaved={() => { setCreando(false); loadRows(); loadResumen() }}
        />
      )}
    </div>
  )
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div className="card" style={{ background: 'var(--surface-2, #fff)', width: '100%', maxWidth: 440, padding: 20, borderRadius: 12 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--n-400)' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--n-500)', display: 'block', marginBottom: 4, marginTop: 12 }

function EditarCuentaModal({ cuenta, onClose, onSaved }: { cuenta: Cuenta; onClose: () => void; onSaved: () => void }) {
  const [nombre, setNombre] = useState(cuenta.nombre)
  const [naturaleza, setNaturaleza] = useState(cuenta.naturaleza)
  const [acepta, setAcepta] = useState(cuenta.acepta_partidas)
  const [activa, setActiva] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const conMov = cuenta.n_partidas > 0

  const save = async () => {
    setSaving(true); setErr(null)
    const { error } = await supabase.rpc('actualizar_cuenta_contable_admin', {
      p_codigo: cuenta.codigo, p_nombre: nombre, p_naturaleza: naturaleza,
      p_acepta_partidas: acepta, p_activa: activa, p_notas: null,
    })
    setSaving(false)
    if (error) setErr(error.message)
    else onSaved()
  }

  return (
    <ModalShell title={`Corregir ${cuenta.codigo}`} onClose={onClose}>
      {conMov && <div style={{ fontSize: 12, color: 'var(--brand-700, #C2410C)', background: 'var(--brand-50, #FFF1EB)', padding: 8, borderRadius: 8, marginBottom: 8 }}>
        Esta cuenta tiene {cuenta.n_partidas} movimientos: solo se puede corregir el nombre.
      </div>}
      <label style={lbl}>Nombre</label>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} style={{ width: '100%', height: 36 }} />
      <label style={lbl}>Naturaleza</label>
      <select value={naturaleza} onChange={(e) => setNaturaleza(e.target.value)} disabled={conMov} style={{ width: '100%', height: 36 }}>
        <option value="DEUDORA">DEUDORA</option>
        <option value="ACREEDORA">ACREEDORA</option>
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12 }}>
        <input type="checkbox" checked={acepta} disabled={conMov} onChange={(e) => setAcepta(e.target.checked)} /> Acepta partidas (cuenta de detalle)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 8 }}>
        <input type="checkbox" checked={activa} disabled={conMov} onChange={(e) => setActiva(e.target.checked)} /> Activa
      </label>
      {err && <div className="error-banner" style={{ margin: '12px 0 0' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
      </div>
    </ModalShell>
  )
}

function CrearCuentaModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [padre, setPadre] = useState('')
  const [naturaleza, setNaturaleza] = useState('DEUDORA')
  const [acepta, setAcepta] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setSaving(true); setErr(null)
    const { error } = await supabase.rpc('crear_cuenta_contable_admin', {
      p_codigo: codigo.trim(), p_nombre: nombre, p_padre_codigo: padre.trim() || null,
      p_naturaleza: naturaleza, p_acepta_partidas: acepta,
    })
    setSaving(false)
    if (error) setErr(error.message)
    else onSaved()
  }

  return (
    <ModalShell title="Nueva cuenta" onClose={onClose}>
      <label style={lbl}>Código (solo dígitos, ej. 10310107)</label>
      <input value={codigo} onChange={(e) => setCodigo(e.target.value)} style={{ width: '100%', height: 36, fontFamily: 'monospace' }} />
      <label style={lbl}>Nombre</label>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} style={{ width: '100%', height: 36 }} />
      <label style={lbl}>Código padre (opcional)</label>
      <input value={padre} onChange={(e) => setPadre(e.target.value)} placeholder="ej. 103101" style={{ width: '100%', height: 36, fontFamily: 'monospace' }} />
      <label style={lbl}>Naturaleza</label>
      <select value={naturaleza} onChange={(e) => setNaturaleza(e.target.value)} style={{ width: '100%', height: 36 }}>
        <option value="DEUDORA">DEUDORA</option>
        <option value="ACREEDORA">ACREEDORA</option>
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12 }}>
        <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} /> Acepta partidas (cuenta de detalle)
      </label>
      {err && <div className="error-banner" style={{ margin: '12px 0 0' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn" onClick={save} disabled={saving}>{saving ? 'Creando…' : 'Crear'}</button>
      </div>
    </ModalShell>
  )
}

function Kpi({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 12, color: 'var(--n-500)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{value.toLocaleString('es-GT')}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--n-400)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default NeoCoreCatalogoContablePage
